import {ResidueType} from 'biojs/BioConstants';
import {Sequence} from 'components/Sequence';
import {ASSEMBLY_TYPE} from 'constants/AssemblyConstants';
import {PolymerSubtype, StrandOfDoubleStranded} from 'constants/MolbioConstants';
import {SequenceTopology} from 'constants/SequenceConstants';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {shuffledPastelColors} from 'lib/Colors';
import Protein from 'molbio/aa-sequences/AaSequence';
import {BulkAssemblyFileQuery} from 'molbio/bulk-assembly/backbone/__generated/BulkAssemblyFileQuery';
import {
  BulkAssemblyLayoutError,
  BulkAssemblyValidationMessages,
  DisplayPrimer,
} from 'molbio/bulk-assembly/BulkAssemblyValidationConstants';
import {
  BAIdValue,
  BANumberValue,
  BANumericIdValue,
  BAOrientationValue,
  BARawTextValue,
  BulkAssemblyOrientation,
} from 'molbio/bulk-assembly/BulkAssemblyValues';
import {BulkAssemblyConstructsCardError} from 'molbio/bulk-assembly/card/BulkAssemblyCard';
import {VisualPolymer} from 'molbio/components/visualizations/PolymerPreviewConstants';
import {SharedModalProps} from 'ui-library/modals/Modal';

type BulkAssemblyGQL = ExtractByTypename<BulkAssemblyFileQuery['bulkAssembly'], 'BulkAssemblyNode'>;

export type {BulkAssemblyGQL};

const MAX_CONSTRUCT_COUNT = 5000;
export const MAX_TABLE_SIZE = 5000;
// Keep in sync with MAX_CONSTRUCT_FRAGMENT_COUNT in benchling/molbio/bulk_assembly/domain/models/product_limits.py
export const MIN_FRAGMENT_BINS_COUNT = 2;
// Keep in sync with constant of same name in benchling/molbio/bulk_assembly/domain/models/product_limits.py
const MAX_FRAGMENT_BIN_COUNT = 15;
// Pulled out for testing
export function getMaxFragmentBinCount(): number {
  return MAX_FRAGMENT_BIN_COUNT;
}
export function getMaxConstructCount(): number {
  return MAX_CONSTRUCT_COUNT;
}

export enum BulkAssemblyOligoSource {
  DESIGNED = 'DESIGNED',
  PREFERRED_PRIMER = 'PREFERRED_PRIMER',
}

export enum AmbiguousConstructPreference {
  USE_LARGER_FRAGMENT_IN_FIRST_BIN = 'USE_LARGER_FRAGMENT_IN_FIRST_BIN',
  USE_SMALLER_FRAGMENT_IN_FIRST_BIN = 'USE_SMALLER_FRAGMENT_IN_FIRST_BIN',
}

export enum BulkAssemblyType {
  CLONING = 'CLONING',
  CONCATENATION = 'CONCATENATION',
}

// Note that this is quite similar to SequenceTopology in SequenceConstants.ts but uses all caps for the enum values.
export enum BulkAssemblyTopology {
  CIRCULAR = 'CIRCULAR',
  LINEAR = 'LINEAR',
}

/**
 * Polymer information for presenting and validating information in the fragments table.
 */
export interface BasicPolymerInfo {
  residues: string;
  circular: boolean;
  startIndex: number;
  singleStranded: boolean;
  residueType: ResidueType;
}

export enum BulkAssemblyBinType {
  FRAGMENT = 'FRAGMENT',
  SPACER = 'SPACER',
}

// Currently, our GraphQL enum generation only handles keys that are the same as values. The backend
// BulkAssemblyTopology has uppercase keys and lowercase values. Rather than overhaul that, we translate
// manually between layers :(
export const BULK_ASSEMBLY_TOPOLOGY_TO_SEQUENCE_TOPOLOGY: {[key in BulkAssemblyTopology]: SequenceTopology} =
  {
    [BulkAssemblyTopology.CIRCULAR]: SequenceTopology.CIRCULAR,
    [BulkAssemblyTopology.LINEAR]: SequenceTopology.LINEAR,
  };

export const SEQUENCE_TOPOLOGY_TO_BULK_ASSEMBLY_TOPOLOGY: {[key in SequenceTopology]: BulkAssemblyTopology} =
  {
    [SequenceTopology.CIRCULAR]: BulkAssemblyTopology.CIRCULAR,
    [SequenceTopology.LINEAR]: BulkAssemblyTopology.LINEAR,
  };

export interface BulkAssembly extends BulkAssemblyGQL {
  // Narrow procedureJson to be non-null.
  procedureJson: BulkAssemblyProcedure;
}

/**
 * Core information that the user has specified about a fragment.
 */
export interface NormalizedFragment {
  cid: string;
  sequence: BAIdValue | BARawTextValue;
  // Lock in the version ID at the time the fragment sequence was specified.
  sequenceVersionDbId: number | null;
  protein: BAIdValue | BARawTextValue;
  // Lock in the version ID at the time the fragment protein was specified.
  proteinVersionDbId: number | null;
  bin: BAIdValue | BARawTextValue;
  start: BANumberValue | BARawTextValue;
  end: BANumberValue | BARawTextValue;
  orientation: BAOrientationValue | BARawTextValue;
  // For fragments where preferred primers don't apply, these values are BA_EMPTY_VALUE.
  preferredPrimer5: BAIdValue | BARawTextValue;
  preferredPrimer3: BAIdValue | BARawTextValue;
  restrictionEnzyme5: BANumericIdValue | BARawTextValue;
  restrictionEnzyme3: BANumericIdValue | BARawTextValue;
}

export interface EnzymeCutsiteData {
  restrictionEnzymeId: number;
  cutsites: Array<number>;
  modelToDisplayConverters: {
    start: (value: number) => number;
    end: (value: number) => number;
  };
  orientation: BulkAssemblyOrientation;
}

/**
 * Fragment with values filled out for passing into the fragments table.
 */
export interface TableFragment {
  cid: string;
  bin: {text: string; error?: string};
  start: {text: string; error?: string; locked?: boolean};
  end: {text: string; error?: string; locked?: boolean};
  length: {text: string};
  orientation: {text: string; error?: string; locked?: boolean};
  polymer: {text: string; id?: string};
  polymerVersionId: number | null;
  validationMessages: BulkAssemblyValidationMessages;
  fragmentProductionMethod: {text: string} | null;
  preferredPrimer5: {text: string; id?: string; locked?: boolean};
  preferredPrimer3: {text: string; id?: string; locked?: boolean};
  restrictionEnzyme5: {text: string; error?: string; locked?: boolean};
  restrictionEnzyme3: {text: string; error?: string; locked?: boolean};
  enzymeCutsiteData5: EnzymeCutsiteData | null;
  enzymeCutsiteData3: EnzymeCutsiteData | null;
}

/**
 * Information about a fragment that has a sequence and bin specified.
 */
export interface SpecifiedFragmentInfo {
  fragmentCid: string;
  binCid: string;
  /**
   * The name of the polymer fragment, or the empty string if it is loading. In cases where
   * there are multiple fragments with identical names are in the same bin, the fragment range -
   * ([start]-[end]) - will be tacked on to the end of the name.
   * */
  polymerName: string;
  /**
   * SpecifiedFragmentInfos have a resolved sequence and bin, but may have invalid endpoints. This value holds
   * error messages about those values.
   */
  errors: Array<string>;
}

/**
 * Fragment as specified when making a table edit.
 *
 * cid is optional since a row may be unspecified.
 */
export type FragmentUpdate = Omit<
  NormalizedFragment,
  'cid' | 'sequence' | 'protein' | 'proteinVersionDbId' | 'sequenceVersionDbId'
> & {cid: string | null; polymer: BAIdValue | BARawTextValue; polymerVersionDbId: number | null};

interface BulkAssemblyBaseBin {
  cid: string;
  name: string;
  binType: BulkAssemblyBinType;
}

export interface BulkAssemblyFragmentBin extends BulkAssemblyBaseBin {
  color: string;
  binType: BulkAssemblyBinType.FRAGMENT;
  fragmentProductionMethod: FragmentProductionMethod;
}

export function isBulkAssemblyFragmentBin(bin: BulkAssemblyBin): bin is BulkAssemblyFragmentBin {
  return bin.binType === BulkAssemblyBinType.FRAGMENT;
}

export interface BulkAssemblySpacerBin extends BulkAssemblyBaseBin {
  residues: string;
  binType: BulkAssemblyBinType.SPACER;
}

export function isBulkAssemblySpacerBin(bin: BulkAssemblyBin): bin is BulkAssemblySpacerBin {
  return bin.binType === BulkAssemblyBinType.SPACER;
}

export type BulkAssemblyBin = BulkAssemblyFragmentBin | BulkAssemblySpacerBin;

export interface DisplayFragmentBin extends BulkAssemblyFragmentBin {
  numBinFragments: number;
}

export type DisplayBin = DisplayFragmentBin | BulkAssemblySpacerBin;

/**
 * Information about the choice of fragment as part of a construct.
 *
 * Currently, this only contains the fragment ID, but this will contain primer config in the future.
 */
export interface ConstructFragment {
  fragment: BAIdValue | BARawTextValue;
}

/**
 * Core information that the user has specified about a construct.
 */
export interface NormalizedConstruct {
  cid: string;
  constructFragmentsByBinId: {[binId: string]: ConstructFragment};
}

/**
 * Derived construct information for display in the constructs tab.
 */
export interface PreviewConstruct {
  cid: string;
  name: string;
  // An object for visualizing the construct, if a construct sequence could be generated.
  visualPolymer: VisualPolymer | null;
  fragmentCids: Array<string>;
  fragmentNames: Array<string>;
  primers: Array<DisplayPrimer>;
  hasErrors: boolean;
  hasWarnings: boolean;
  outputPolymerId: string | null;
}

/**
 * Derived information about a fragment as part of a construct.
 */
export interface TableConstructFragment {
  fragmentCid: string | null;
  fragmentName: string;
  fragmentEndpoints: string | null;
  overhangBases: string;
  overlapLength: string | null;
  error?: string;
}

/**
 * Derived construct information for display in the constructs table.
 */
export interface TableConstruct extends NormalizedConstruct {
  // For finalized assemblies, this is the ID of the sequence to link to; otherwise null.
  createdSequenceId: string | null;
  name: string;
  tableConstructFragmentByBinId: {
    [binId: string]: TableConstructFragment;
  };
  validationMessages: BulkAssemblyValidationMessages;
  /** The construct can be opened in the construct preview modal.
   * This doesn't necessarily mean the construct has a valid sequence preview. */
  canPreview: boolean;
}

/**
 * Information provided from the constructs table when updating a construct.
 */
export type ConstructUpdate = Omit<NormalizedConstruct, 'cid'> & {cid: string | null};

export interface BulkAssemblyProcedure {
  method: MethodData;
  topology: BulkAssemblyTopology;
  binCidOrdering: Array<string>;
  binsByCid: {[binCid: string]: BulkAssemblyBin};
  fragments: Array<NormalizedFragment>;
  constructs: Array<NormalizedConstruct>;
}

export interface GibsonMethodData {
  type: BulkAssemblyMethod.GIBSON;
  parameters: GibsonParameters;
}

export interface GoldenGateMethodData {
  type: BulkAssemblyMethod.GOLDEN_GATE;
  parameters: GoldenGateParameters;
}

export interface HomologyMethodData {
  type: BulkAssemblyMethod.HOMOLOGY;
  parameters: HomologyParameters;
}

export interface ConcatenationMethodData {
  type: BulkAssemblyMethod.CONCATENATION;
  parameters: ConcatenationParameters;
}

export type MethodData =
  | GibsonMethodData
  | GoldenGateMethodData
  | HomologyMethodData
  | ConcatenationMethodData;

export type CloningMethodData = Exclude<MethodData, ConcatenationMethodData>;

// TODO(BNCH-69125) Add restriction cloning
export type RestrictionEnzymeBulkAssemblyMethodData = GoldenGateMethodData;

// TODO(BNCH-69125) Add restriction cloning
export function isRestrictionEnzymeBulkAssemblyType(
  methodData: MethodData
): methodData is RestrictionEnzymeBulkAssemblyMethodData {
  return methodData.type === BulkAssemblyMethod.GOLDEN_GATE || methodData.type === BulkAssemblyMethod.GIBSON;
}

// Keep in sync with benchling.molbio.bulk_assembly.domain.assembly_enums
export enum BulkAssemblyMethod {
  GOLDEN_GATE = 'GOLDEN_GATE',
  GIBSON = 'GIBSON',
  HOMOLOGY = 'HOMOLOGY',
  CONCATENATION = 'CONCATENATION',
}

export type SpacerSupportingBulkAssemblyType =
  | BulkAssemblyMethod.CONCATENATION
  | BulkAssemblyMethod.GIBSON
  | BulkAssemblyMethod.GOLDEN_GATE;

export function isSpacerSupportingBulkAssemblyType(
  bulkAssemblyType: BulkAssemblyMethod
): bulkAssemblyType is SpacerSupportingBulkAssemblyType {
  return [
    BulkAssemblyMethod.CONCATENATION,
    BulkAssemblyMethod.GIBSON,
    BulkAssemblyMethod.GOLDEN_GATE,
  ].includes(bulkAssemblyType);
}

export type CloningBulkAssemblyMethod = Exclude<BulkAssemblyMethod, BulkAssemblyMethod.CONCATENATION>;

export const METHOD_TO_DISPLAY: {
  [method in BulkAssemblyMethod]: string;
} = {
  [BulkAssemblyMethod.GOLDEN_GATE]: 'Golden Gate',
  [BulkAssemblyMethod.GIBSON]: 'Gibson',
  [BulkAssemblyMethod.HOMOLOGY]: 'Homology',
  [BulkAssemblyMethod.CONCATENATION]: 'Concatenation',
};

export const METHOD_TO_SPACER_NAME: {
  [method in SpacerSupportingBulkAssemblyType]: string;
} = {
  [BulkAssemblyMethod.GOLDEN_GATE]: 'spacer',
  [BulkAssemblyMethod.GIBSON]: 'spacer',
  [BulkAssemblyMethod.CONCATENATION]: 'constant',
};

export type ConvertedLegacyAssemblyType = typeof ASSEMBLY_TYPE.GIBSON | typeof ASSEMBLY_TYPE.GOLDEN_GATE;

export function isConvertedAssemblyType(
  assemblyType: ASSEMBLY_TYPE
): assemblyType is ConvertedLegacyAssemblyType {
  return assemblyType === ASSEMBLY_TYPE.GIBSON || assemblyType === ASSEMBLY_TYPE.GOLDEN_GATE;
}

export const LEGACY_ASSEMBLY_TYPE_TO_BULK_ASSEMBLY_METHOD: {
  [assemblyType in ConvertedLegacyAssemblyType]: CloningBulkAssemblyMethod;
} = {
  [ASSEMBLY_TYPE.GOLDEN_GATE]: BulkAssemblyMethod.GOLDEN_GATE,
  [ASSEMBLY_TYPE.GIBSON]: BulkAssemblyMethod.GIBSON,
};

export enum FragmentProductionMethod {
  EXISTING_CUT_SITES = 'EXISTING_CUT_SITES',
  PRIMER_PAIR = 'PRIMER_PAIR',
  EXISTING_HOMOLOGY_REGIONS = 'EXISTING_HOMOLOGY_REGIONS',
  // Special method with no biological significance currently used for Concatenation assemblies.
  // It is essentially treated as a literal subsequence of the input specified by endpoints.
  SLICE = 'SLICE',
}

export enum DisplayOnlyFragmentProductionMethod {
  VARIABLE = 'VARIABLE',
}

export type DisplayFragmentProductionMethod = FragmentProductionMethod | DisplayOnlyFragmentProductionMethod;

export const BULK_ASSEMBLY_METHOD_TO_FRAGMENT_PRODUCTION_METHOD_TO_DISPLAY_PARAMS: {
  [bulkAssemblyMethod in BulkAssemblyMethod]: {
    [fragmentProductionMethod in DisplayFragmentProductionMethod]: {display: string; details: string} | null;
  };
} = {
  [BulkAssemblyMethod.GOLDEN_GATE]: {
    [FragmentProductionMethod.EXISTING_CUT_SITES]: {
      display: 'Use existing cut sites',
      details: 'Digest fragments at Type IIS enzyme cut sites to expose overhangs.',
    },
    [FragmentProductionMethod.PRIMER_PAIR]: {
      display: 'Use a primer pair',
      details: 'Use a primer pair to introduce Type IIS enzyme cut sites and overhangs.',
    },
    [FragmentProductionMethod.EXISTING_HOMOLOGY_REGIONS]: null,
    [FragmentProductionMethod.SLICE]: null,
    [DisplayOnlyFragmentProductionMethod.VARIABLE]: {
      display: 'Variable',
      details: 'Use a mix of both methods above.',
    },
  },
  [BulkAssemblyMethod.GIBSON]: {
    [FragmentProductionMethod.EXISTING_CUT_SITES]: {
      display: 'Digest with restriction enzyme',
      details: 'Linearize fragments at restriction enzyme cut sites.',
    },
    [FragmentProductionMethod.PRIMER_PAIR]: {
      display: 'Add new overlaps using PCR',
      details: 'Use a primer pair to generate new homology regions.',
    },
    [FragmentProductionMethod.EXISTING_HOMOLOGY_REGIONS]: {
      display: 'Find existing overlaps',
      details: 'Use homology regions that have already been incorporated.',
    },
    [FragmentProductionMethod.SLICE]: null,
    [DisplayOnlyFragmentProductionMethod.VARIABLE]: {
      display: 'Variable',
      details: 'Use a mix of the methods above.',
    },
  },
  // Homology details are not displayed anywhere right now.
  [BulkAssemblyMethod.HOMOLOGY]: {
    [FragmentProductionMethod.EXISTING_CUT_SITES]: null,
    [FragmentProductionMethod.PRIMER_PAIR]: null,
    [FragmentProductionMethod.EXISTING_HOMOLOGY_REGIONS]: {
      display: 'Use existing homology regions',
      details: 'Use existing homology regions to join fragments',
    },
    [FragmentProductionMethod.SLICE]: null,
    [DisplayOnlyFragmentProductionMethod.VARIABLE]: null,
  },
  // Concatenation details are not displayed anywhere right now.
  [BulkAssemblyMethod.CONCATENATION]: {
    [FragmentProductionMethod.EXISTING_CUT_SITES]: null,
    [FragmentProductionMethod.PRIMER_PAIR]: null,
    [FragmentProductionMethod.EXISTING_HOMOLOGY_REGIONS]: null,
    [FragmentProductionMethod.SLICE]: {
      display: '',
      details: '',
    },
    [DisplayOnlyFragmentProductionMethod.VARIABLE]: null,
  },
};

export const PRIMERS_FRAGMENT_PRODUCTION_METHODS: Array<DisplayFragmentProductionMethod> = [
  FragmentProductionMethod.PRIMER_PAIR,
  DisplayOnlyFragmentProductionMethod.VARIABLE,
];

export type GoldenGateFragmentProductionMethod =
  | FragmentProductionMethod.EXISTING_CUT_SITES
  | FragmentProductionMethod.PRIMER_PAIR;

export interface GoldenGateParameters {
  defaultRestrictionEnzymeId: number;
  defaultFragmentProductionMethod: GoldenGateFragmentProductionMethod;
  preRecognitionSiteLength: number | null;
  preRecognitionSiteBases: string;
  bindingRegionMinMeltingTemp: number | null;
  bindingRegionMinLength: number | null;
  primerPairMaxMeltingTempDifference: number | null;
}

export type GibsonFragmentProductionMethod =
  | FragmentProductionMethod.PRIMER_PAIR
  | FragmentProductionMethod.EXISTING_HOMOLOGY_REGIONS
  | FragmentProductionMethod.EXISTING_CUT_SITES;

export interface GibsonParameters {
  primerMinMeltingTemp: number | null;
  primerPairMaxMeltingTempDifference: number | null;
  bindingRegionMinMeltingTemp: number | null;
  bindingAndHomologyRegionMinLength: number | null;
  bindingAndHomologyRegionMaxLength: number | null;
  defaultFragmentProductionMethod: GibsonFragmentProductionMethod;
}

export interface HomologyParameters {
  defaultFragmentProductionMethod: FragmentProductionMethod.EXISTING_HOMOLOGY_REGIONS;
  ambiguousConstructPreference: AmbiguousConstructPreference;
}

export interface ConcatenationParameters {
  polymerSubtype: PolymerSubtype;
  defaultFragmentProductionMethod: FragmentProductionMethod.SLICE;
  // Only applicable when the construct type supports a default null terminal phosphate, which is
  // currently just oligos.
  applyDefaultNullTerminalPhosphate: boolean;
}

export type MethodParameters =
  | GoldenGateParameters
  | GibsonParameters
  | HomologyParameters
  | ConcatenationParameters;

export const DEFAULT_GOLDEN_GATE_PARAMETERS = {
  defaultRestrictionEnzymeId: 85, // BbsI
  defaultFragmentProductionMethod: FragmentProductionMethod.EXISTING_CUT_SITES,
  preRecognitionSiteLength: 6,
  preRecognitionSiteBases: '',
  bindingRegionMinMeltingTemp: 50,
  bindingRegionMinLength: 18,
  primerPairMaxMeltingTempDifference: 5,
} satisfies GoldenGateParameters;

export const DEFAULT_GIBSON_PARAMETERS = {
  defaultFragmentProductionMethod: FragmentProductionMethod.PRIMER_PAIR,
  bindingRegionMinMeltingTemp: 50,
  primerMinMeltingTemp: 60,
  bindingAndHomologyRegionMinLength: 20,
  bindingAndHomologyRegionMaxLength: 30,
  primerPairMaxMeltingTempDifference: 5,
} satisfies GibsonParameters;

export const DEFAULT_HOMOLOGY_PARAMETERS = {
  defaultFragmentProductionMethod: FragmentProductionMethod.EXISTING_HOMOLOGY_REGIONS,
  ambiguousConstructPreference: AmbiguousConstructPreference.USE_LARGER_FRAGMENT_IN_FIRST_BIN,
} satisfies HomologyParameters;

export const DEFAULT_CONCATENATION_PARAMETERS = {
  polymerSubtype: PolymerSubtype.dna_sequence,
  defaultFragmentProductionMethod: FragmentProductionMethod.SLICE,
  applyDefaultNullTerminalPhosphate: true,
} satisfies ConcatenationParameters;

export const CLONING_BULK_ASSEMBLY_METHOD_TO_DEFAULT_METHOD_DATA: {
  [key in CloningBulkAssemblyMethod]: CloningMethodData;
} = {
  [BulkAssemblyMethod.GIBSON]: {
    type: BulkAssemblyMethod.GIBSON,
    parameters: DEFAULT_GIBSON_PARAMETERS,
  },
  [BulkAssemblyMethod.GOLDEN_GATE]: {
    type: BulkAssemblyMethod.GOLDEN_GATE,
    parameters: DEFAULT_GOLDEN_GATE_PARAMETERS,
  },
  [BulkAssemblyMethod.HOMOLOGY]: {
    type: BulkAssemblyMethod.HOMOLOGY,
    parameters: DEFAULT_HOMOLOGY_PARAMETERS,
  },
};

export enum PreviousTool {
  LEGACY_BULK_ASSEMBLY = 'LEGACY_BULK_ASSEMBLY',
  ASSEMBLY_WIZARD = 'ASSEMBLY_WIZARD',
}

export const FALLBACK_VISUALIZATION_COLOR = shuffledPastelColors[0];
export const FALLBACK_ORIENTATION: BulkAssemblyOrientation = BulkAssemblyOrientation.FORWARD;

export const OPPOSITE_BA_ORIENTATION: Record<BulkAssemblyOrientation, BulkAssemblyOrientation> = {
  [BulkAssemblyOrientation.FORWARD]: BulkAssemblyOrientation.REVERSE,
  [BulkAssemblyOrientation.REVERSE]: BulkAssemblyOrientation.FORWARD,
};

export const BA_ORIENTATION_TO_STRAND: Record<BulkAssemblyOrientation, StrandOfDoubleStranded> = {
  [BulkAssemblyOrientation.FORWARD]: 1,
  [BulkAssemblyOrientation.REVERSE]: -1,
};

export interface Endpoints {
  start: BANumberValue | BARawTextValue;
  end: BANumberValue | BARawTextValue;
}

export interface ErrorsByBin {
  [binCid: string]: Set<BulkAssemblyLayoutError>;
}

export interface LayoutValidationStatus {
  errorsByBin: ErrorsByBin;
  otherErrors: Array<BulkAssemblyConstructsCardError>;
}

export interface SettingsModalBulkAssemblyParams {
  name: string;
  folderId: string;
  procedureJson: BulkAssemblyProcedure;
  studyIds: Array<string>;
}

export const BulkAssemblyPreferences = {
  GIBSON_PARAMETERS: 'bulkGibsonParameters',
  SHOW_FRAGMENT_DETAILS: 'showFragmentDetails',
  SHOW_OVERLAP_DETAILS: 'showOverlapDetails',
};

export const SKIP_BIN_SENTINEL = 'SKIP_BIN_SENTINEL';
export const SKIP_BIN_DISPLAY_TEXT = 'Skip';

// The version of procedure JSON schema that the client will both read and write.
export const CLIENT_PROCEDURE_JSON_CURRENT_VERSION = '3.0';

export const IMMERSIVE_MODAL_PROPS: Partial<SharedModalProps> = {
  fullWidth: true,
  fullHeight: true,
  scrollable: false,
  bodyPadded: false,
};

export interface SequencePolymerInfo {
  kind: 'sequence';
  ref: {current: Sequence};
}
export interface ProteinPolymerInfo {
  kind: 'protein';
  ref: {current: Protein};
}

export interface RestrictionEnzymeTableInfo {
  text: string;
  locked: boolean;
  error?: string;
}

export interface BinDetails {
  binId: string;
  binName: string;
  binColor: string;
}
